import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Calendar, Users, Phone, MessageSquare,
  Clock, CheckCircle, ArrowRight, ArrowUpRight
} from "lucide-react";
import axios from "axios";
import { toast } from "sonner";
import { format } from "date-fns";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function authHeader() {
  const token = localStorage.getItem("clinic_admin_token");
  return { Authorization: `Bearer ${token}` };
}

const STATUS_STYLES = {
  pending:   "bg-amber-500/10 text-amber-400 border border-amber-500/20",
  confirmed: "bg-blue-500/10 text-blue-400 border border-blue-500/20",
  completed: "bg-green-500/10 text-green-400 border border-green-500/20",
  cancelled: "bg-red-500/10 text-red-400 border border-red-500/20",
};

const STATUS_BAR = {
  pending:   { label: "Pending",   dot: "bg-amber-400" },
  confirmed: { label: "Confirmed", dot: "bg-blue-400" },
  today:     { label: "Today",     dot: "bg-cyan-400" },
  total:     { label: "Total",     dot: "bg-slate-400" },
};

export default function Dashboard() {
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchStats();
  }, []);

  const fetchStats = async () => {
    try {
      const { data } = await axios.get(`${API}/dashboard/stats`, { headers: authHeader() });
      setStats(data);
    } catch {
      toast.error("Failed to load dashboard data");
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="space-y-6">
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          {[...Array(4)].map((_, i) => (
            <div key={i} className="stat-card animate-pulse h-28" />
          ))}
        </div>
      </div>
    );
  }

  const statCards = [
    {
      label: "Total Appointments",
      value: stats?.total_appointments ?? 0,
      sub: `${stats?.today_appointments ?? 0} today`,
      icon: Calendar,
      iconColor: "text-blue-400",
      iconBg: "bg-blue-500/10 border border-blue-500/20",
      accentColor: "from-blue-500",
      borderColor: "border-l-blue-500",
      path: "/admin/appointments",
    },
    {
      label: "Active Patients",
      value: stats?.total_patients ?? 0,
      sub: "registered patients",
      icon: Users,
      iconColor: "text-violet-400",
      iconBg: "bg-violet-500/10 border border-violet-500/20",
      accentColor: "from-violet-500",
      borderColor: "border-l-violet-500",
      path: "/admin/patients",
    },
    {
      label: "Total Calls",
      value: stats?.total_calls ?? 0,
      sub: "via Vapi AI",
      icon: Phone,
      iconColor: "text-cyan-400",
      iconBg: "bg-cyan-500/10 border border-cyan-500/20",
      accentColor: "from-cyan-500",
      borderColor: "border-l-cyan-500",
      path: "/admin/calls",
    },
    {
      label: "WhatsApp Messages",
      value: stats?.total_whatsapp ?? 0,
      sub: "all channels",
      icon: MessageSquare,
      iconColor: "text-emerald-400",
      iconBg: "bg-emerald-500/10 border border-emerald-500/20",
      accentColor: "from-emerald-500",
      borderColor: "border-l-emerald-500",
      path: "/admin/whatsapp",
    },
  ];

  return (
    <div className="space-y-6" data-testid="dashboard-page">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold font-outfit text-white">Dashboard</h1>
        <p className="text-slate-400 text-sm mt-1">
          {format(new Date(), "EEEE, MMMM d, yyyy")}
        </p>
      </div>

      {/* Stat Cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        {statCards.map(s => (
          <button
            key={s.label}
            data-testid={`stat-card-${s.label.toLowerCase().replace(/ /g, "-")}`}
            onClick={() => navigate(s.path)}
            className={`stat-card text-left border-l-4 ${s.borderColor} hover:bg-[#1C2333] transition-all duration-200 hover:-translate-y-0.5 group`}
          >
            <div className="flex items-start justify-between mb-3">
              <div className={`w-9 h-9 rounded-lg ${s.iconBg} flex items-center justify-center`}>
                <s.icon className={`${s.iconColor}`} style={{ width: 18, height: 18 }} />
              </div>
              <ArrowUpRight className="w-4 h-4 text-slate-600 group-hover:text-slate-400 transition-colors" />
            </div>
            <div className="text-2xl font-bold font-outfit text-white">{s.value.toLocaleString()}</div>
            <div className="text-xs font-medium text-slate-300 mt-0.5">{s.label}</div>
            <div className="text-xs text-slate-500 mt-0.5">{s.sub}</div>
          </button>
        ))}
      </div>

      {/* Appointment status row */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        {[
          { label: "Pending",   val: stats?.pending_appointments ?? 0,   dotClass: "bg-amber-400", textClass: "text-amber-400", bgClass: "bg-amber-500/5 border border-amber-500/15" },
          { label: "Confirmed", val: stats?.confirmed_appointments ?? 0, dotClass: "bg-blue-400",  textClass: "text-blue-400",  bgClass: "bg-blue-500/5 border border-blue-500/15" },
          { label: "Today",     val: stats?.today_appointments ?? 0,     dotClass: "bg-cyan-400",  textClass: "text-cyan-400",  bgClass: "bg-cyan-500/5 border border-cyan-500/15" },
          { label: "Total",     val: stats?.total_appointments ?? 0,     dotClass: "bg-slate-400", textClass: "text-slate-300", bgClass: "bg-white/5 border border-white/10" },
        ].map(s => (
          <div key={s.label} className={`rounded-xl px-4 py-3 ${s.bgClass}`}>
            <div className="flex items-center gap-1.5 mb-1">
              <div className={`w-1.5 h-1.5 rounded-full ${s.dotClass}`} />
              <div className="text-xs font-medium uppercase tracking-wider text-slate-500">{s.label}</div>
            </div>
            <div className={`text-2xl font-bold font-outfit ${s.textClass}`}>{s.val}</div>
          </div>
        ))}
      </div>

      {/* Recent tables */}
      <div className="grid md:grid-cols-2 gap-6">
        {/* Recent Appointments */}
        <div className="bg-[#161B27] border border-white/10 rounded-xl overflow-hidden">
          <div className="px-5 py-4 border-b border-white/5 flex items-center justify-between">
            <h2 className="font-semibold font-outfit text-white">Recent Appointments</h2>
            <button
              data-testid="view-all-appointments-btn"
              onClick={() => navigate("/admin/appointments")}
              className="text-xs text-cyan-400 hover:text-cyan-300 flex items-center gap-1 transition-colors"
            >
              View all <ArrowRight className="w-3 h-3" />
            </button>
          </div>
          <div className="divide-y divide-white/5">
            {(stats?.recent_appointments || []).length === 0 ? (
              <div className="px-5 py-8 text-center text-slate-500 text-sm">No appointments yet</div>
            ) : (
              stats.recent_appointments.map(apt => (
                <div key={apt.id} className="px-5 py-3 flex items-center gap-3 hover:bg-white/5 transition-colors">
                  <div className="w-8 h-8 rounded-full bg-blue-500/10 border border-blue-500/20 flex items-center justify-center text-blue-400 font-semibold text-xs">
                    {apt.patient_name?.charAt(0) || "?"}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-medium text-white truncate">{apt.patient_name}</div>
                    <div className="text-xs text-slate-500">{apt.appointment_date} {apt.appointment_time}</div>
                  </div>
                  <span className={`text-xs px-2 py-0.5 rounded-full ${STATUS_STYLES[apt.status] || "bg-slate-500/10 text-slate-400 border border-slate-500/20"}`}>
                    {apt.status}
                  </span>
                </div>
              ))
            )}
          </div>
        </div>

        {/* Recent Calls */}
        <div className="bg-[#161B27] border border-white/10 rounded-xl overflow-hidden">
          <div className="px-5 py-4 border-b border-white/5 flex items-center justify-between">
            <h2 className="font-semibold font-outfit text-white">Recent Calls</h2>
            <button
              data-testid="view-all-calls-btn"
              onClick={() => navigate("/admin/calls")}
              className="text-xs text-cyan-400 hover:text-cyan-300 flex items-center gap-1 transition-colors"
            >
              View all <ArrowRight className="w-3 h-3" />
            </button>
          </div>
          <div className="divide-y divide-white/5">
            {(stats?.recent_calls || []).length === 0 ? (
              <div className="px-5 py-8 text-center text-slate-500 text-sm">No calls yet</div>
            ) : (
              stats.recent_calls.map(call => (
                <div key={call.id} className="px-5 py-3 flex items-center gap-3 hover:bg-white/5 transition-colors">
                  <div className="w-8 h-8 rounded-full bg-cyan-500/10 border border-cyan-500/20 flex items-center justify-center">
                    <Phone className="w-3.5 h-3.5 text-cyan-400" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-medium text-white truncate">
                      {call.caller_phone || "Unknown"}
                    </div>
                    <div className="text-xs text-slate-500">
                      {call.duration ? `${call.duration}s` : "—"} · {call.status}
                    </div>
                  </div>
                  <CheckCircle className="w-4 h-4 text-green-400 flex-shrink-0" />
                </div>
              ))
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
